import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Task, tasksAPI } from '../api/api';
import { useAuth } from '../context/AuthContext';
import TaskModal from './TaskModal';
import './KanbanTaskCard.css';

interface KanbanTaskCardProps {
  task: Task;
  onUpdate: () => void;
  columnName?: string;
}

const KanbanTaskCard: React.FC<KanbanTaskCardProps> = ({ task, onUpdate, columnName }) => {
  const [showModal, setShowModal] = useState(false);
  const { user, isAdmin, isManager } = useAuth();
  
  // Check if user can delete this task (creator, admin, or manager)
  const canDelete = user && (
    task.created_by === user.id || 
    isAdmin || 
    isManager
  );

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent opening modal
    if (!window.confirm('Вы уверены, что хотите удалить эту задачу?')) {
      return;
    }

    try {
      await tasksAPI.delete(task.id);
      onUpdate();
    } catch (error: any) {
      console.error('Ошибка при удалении задачи:', error);
      alert(error.response?.data?.error || 'Не удалось удалить задачу');
    }
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `task-${task.id}`,
    data: {
      type: 'task',
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Проверяем, выполнена ли задача
  const isCompleted = task.status === 'completed';
  // Проверяем, находится ли задача в работе
  const isInWork = (columnName || task.column_name) === 'В работе';

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className={`kanban-task-card ${isCompleted ? 'completed' : isInWork ? 'in-work' : ''}`}
        onClick={() => setShowModal(true)}
        onDragStart={(e) => {
          // Prevent any default drag behavior that might cause issues
          e.dataTransfer.effectAllowed = 'move';
        }}
      >
        <div className="task-card-header">
          <div className="task-card-title">{task.title}</div>
          {canDelete && (
            <button
              onClick={handleDelete}
              className="task-delete-btn"
              title="Удалить задачу"
              onMouseDown={(e) => e.stopPropagation()}
            >
              ×
            </button>
          )}
        </div>
        {task.description && (
          <div className="task-card-description">
            {task.description.length > 100
              ? `${task.description.substring(0, 100)}...`
              : task.description}
          </div>
        )}
        <div className="task-card-footer">
          <div className="task-assigned-info">
            {task.assigned_to_username && (
              <span className="task-assigned">👤 {task.assigned_to_username}</span>
            )}
            {task.deadline && (
              <span 
                className="task-deadline"
                style={{ 
                  color: new Date(task.deadline) < new Date() ? '#e74c3c' : '#666',
                  fontWeight: new Date(task.deadline) < new Date() ? 'bold' : 'normal'
                }}
              >
                ⏰ {new Date(task.deadline).toLocaleDateString('ru-RU', { 
                  day: '2-digit', 
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            )}
          </div>
          <div className="task-stats">
            {task.comments && task.comments.length > 0 && (
              <span className="stat">💬 {task.comments.length}</span>
            )}
            {task.attachments && task.attachments.length > 0 && (
              <span className="stat">📎 {task.attachments.length}</span>
            )}
            {isInWork && !isCompleted && (
              <span className="in-work-indicator" title="Задача в работе">
                ⚙️
              </span>
            )}
            {isCompleted && (
              <span className="completed-indicator" title="Задача выполнена">
                ✅
              </span>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <TaskModal
          taskId={task.id}
          onClose={() => {
            setShowModal(false);
          }}
          onTaskUpdated={onUpdate}
        />
      )}
    </>
  );
};

export default KanbanTaskCard;


